import React from 'react';
import { NavLink } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { VideoSection } from '../components/VideoSection';
import {
  COMPANY_INFO,
  CLICK_STEPS,
  SITE_IMAGES,
  getCdnImage,
} from '../data/content';

export function SadanByggerDu() {
  const PROCESS_STEPS = [
    {
      num: '01',
      title: 'Projektet planlægges',
      desc: 'Tegninger gennemgås og tilpasses modulmålene i SYSTEM 3E. Du får fuld overblik over elementbehov og tidsplan.',
      icon: 'fa-solid fa-compass-drafting',
    },
    {
      num: '02',
      title: 'Nødvendige elementer beregnes',
      desc: 'Projektets præcise elementtyper beregnes digitalt, så der leveres nøjagtigt de 6 elementvarianter, der skal bruges.',
      icon: 'fa-solid fa-calculator',
    },
    {
      num: '03',
      title: 'Elementerne leveres direkte',
      desc: 'Pallerne med perlitelementer leveres klar til brug på byggepladsen. Ingen opbevaring af poser med lim eller mørtel.',
      icon: 'fa-solid fa-truck-ramp-box',
    },
    {
      num: '04',
      title: 'Placering & Click-Click samling',
      desc: 'Første skift placeres i vater. Derefter klikkes elementerne sammen lag for lag uden tørretid eller vand.',
      icon: 'fa-solid fa-cubes',
    },
    {
      num: '05',
      title: 'Systematisk vægopbygning',
      desc: 'Væggene skyder hurtigt i vejret. Hjørner, overliggere og vinduesåbninger formes ubesværet med de matchende specialelementer.',
      icon: 'fa-solid fa-layer-group',
    },
    {
      num: '06',
      title: 'Færdiggørelse af bygningsdele',
      desc: 'Væggen er øjeblikkeligt klar til tagkonstruktion, pudsning eller indvendig overfladebehandling uden lange ventetider.',
      icon: 'fa-solid fa-house-circle-check',
    },
  ];

  return (
    <>
      <SEO
        title="Sådan bygger du med SYSTEM 3E"
        description="Lær hvordan du opfører vægge med Click-Click samling i 6 enkle trin. Både private og håndværkere bygger op til 3-4 gange hurtigere. Se videodemonstration her."
      />

      {/* Hero */}
      <section className="bg-[#243447] text-white py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getCdnImage(SITE_IMAGES.constructionSite, 1600, 80)}
            alt="Byggeplads med SYSTEM 3E"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-[#162332]/45" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#819FA8] font-extrabold text-xs uppercase tracking-widest block mb-2">
              Pædagogisk Guidet Proces
            </span>
            <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight mb-6">
              Sådan bygger du med SYSTEM 3E
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              Når man først ser systemet i funktion, forstår man det med det samme. Enkel, ren og præcis vægopbygning fra fundament til tag.
            </p>
          </div>
        </div>
      </section>

      {/* Video Demonstration Prominently Placed */}
      <VideoSection
        title="The Click Click Method is Genius"
        subtitle="Se hvor ubesværet elementerne klikker sammen på få sekunder. Ingen traditionel mørtel, ingen lim, ingen vandbaserede samlinger."
      />

      {/* Click Click Core 3 Steps */}
      <section className="py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-red-600 font-extrabold text-xs uppercase tracking-widest block mb-2">
              Kerneprincippet
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Mekanisk montage i 3 trin
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {CLICK_STEPS.map((step, idx) => (
              <div
                key={idx}
                className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="h-48 overflow-hidden bg-slate-900 relative">
                    <img
                      src={step.image}
                      alt={step.alt || step.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-red-600 text-white font-black text-sm px-3.5 py-1 rounded-full shadow">
                      {step.step}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-black text-slate-900 mb-1">{step.title}</h3>
                    <p className="text-red-600 font-bold text-xs uppercase mb-3">
                      {step.subtitle}
                    </p>
                    <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 Step Full Workflow */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-red-600 font-extrabold text-xs uppercase tracking-widest block mb-2">
              Fra Idé til Råhus
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              De 6 faser i et SYSTEM 3E byggeprojekt
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {PROCESS_STEPS.map((step, idx) => (
              <div
                key={idx}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-6 relative flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-red-600/10 text-red-600 rounded-xl flex items-center justify-center text-xl">
                      <i className={step.icon} aria-hidden="true" />
                    </div>
                    <span className="text-2xl font-black text-slate-300">{step.num}</span>
                  </div>
                  <h3 className="font-extrabold text-slate-900 text-lg mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-4xl font-black text-white mb-4">
            Vil du opleve processen tæt på?
          </h2>
          <p className="text-slate-300 text-base max-w-xl mx-auto mb-8">
            Kontakt {COMPANY_INFO.name} for at drøfte dit byggeprojekt eller modtage en prøve og rådgivning.
          </p>
          <NavLink
            to="/kontakt"
            className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 rounded-xl text-base transition-all inline-block shadow-lg"
          >
            Kontakt den danske eneforhandler
          </NavLink>
        </div>
      </section>
    </>
  );
}
